/** A navegação do celular, no polegar (DESIGN.md § 2.2).
 *
 * O documento promete isto desde a fundação — "navegação inferior com 5
 * destinos" —, e o que existia era a barra do desktop quebrando em três linhas.
 * Somada ao aviso de verificação, ela comia cerca de 290px de 844 antes de
 * qualquer conteúdo: um terço da tela, num produto que se declara
 * WhatsApp-First e portanto vive no celular.
 *
 * **Nada some.** Os destinos que não cabem nos cinco vão para "Mais", e não
 * para lugar nenhum — esconder Auditoria ou Indicadores do celular seria trocar
 * um problema de densidade por um de acesso.
 *
 * Os cinco não são os do § 2.2 ao pé da letra: "Hoje" e "Triagem" ainda não
 * existem como tela. Ficam Funil, Agenda, Criar, Fechadas e Mais — e quando
 * "Hoje" nascer, ele toma o lugar que é dele, à esquerda.
 */

import type { ReactNode } from "react";

export interface DestinoDaNavegacao {
  /** Como aparece embaixo do ícone. Uma palavra: duas quebram em 375px. */
  rotulo: string;
  icone: ReactNode;
  ativo?: boolean;
  aoAcionar: () => void;
}

export function NavegacaoInferior({
  destinos,
  aoCriar,
}: {
  destinos: readonly DestinoDaNavegacao[];
  /** O botão central. Criar oportunidade é o que mais se faz no celular, e por
   * isso ele é o único com peso de cor aqui. */
  aoCriar: () => void;
}) {
  const [inicio, fim] = [destinos.slice(0, 2), destinos.slice(2)];

  return (
    // `sm:hidden`: acima de 640px a barra superior dá conta, e duas navegações
    // ao mesmo tempo seriam duas respostas para "onde eu estou".
    //
    // `pb-[env(safe-area-inset-bottom)]`: no iPhone a faixa do gesto de início
    // fica por cima do que estiver colado no fim da tela.
    <nav
      aria-label="Navegação principal"
      className="fixed inset-x-0 bottom-0 z-30 flex items-stretch justify-around border-t border-borda bg-superficie pb-[env(safe-area-inset-bottom)] sm:hidden"
    >
      {inicio.map((destino) => (
        <Destino key={destino.rotulo} {...destino} />
      ))}

      <button
        type="button"
        onClick={aoCriar}
        aria-label="Nova oportunidade"
        // Mesmo `data-guia` do botão equivalente no cabeçalho do funil
        // (`QuadroDeOportunidades.tsx`): o guia aponta para "nova-oportunidade"
        // em qualquer largura de tela, e cada um dos dois só tem tamanho na
        // metade em que aparece (2026-09-14) — ver `Guia.tsx::escolherRetangulo`.
        data-guia="nova-oportunidade"
        className="-mt-3 grid h-12 w-12 shrink-0 place-items-center self-center rounded-full bg-acao text-acao-contraste shadow-e2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
      >
        <span aria-hidden="true" className="font-display text-display-md leading-none">
          +
        </span>
      </button>

      {fim.map((destino) => (
        <Destino key={destino.rotulo} {...destino} />
      ))}
    </nav>
  );
}

function Destino({ rotulo, icone, ativo, aoAcionar }: DestinoDaNavegacao) {
  return (
    <button
      type="button"
      onClick={aoAcionar}
      // `aria-current="page"` e não só a cor: quem usa leitor de tela precisa
      // saber onde está, e cor não é o único sinal (DESIGN.md § 8).
      aria-current={ativo === true ? "page" : undefined}
      className={[
        "flex min-w-[3.5rem] flex-1 flex-col items-center gap-0.5 px-1 py-2",
        "font-texto text-micro",
        ativo === true ? "text-acao" : "text-texto-suave",
        "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-acao-viva",
      ].join(" ")}
    >
      <span aria-hidden="true" className="text-body leading-none">
        {icone}
      </span>
      {rotulo}
    </button>
  );
}
